import { API, getCompany } from '@/src/api/api'
import { useProductAnalytics } from '@/src/api/data/analytics'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { useDashboardEditingStore } from '@/src/features/dashboard/infrastructure/useDashboardEditingStore'
import type { LocationModule } from '@/src/hooks/location-module/location-module.types'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { clearEmberThreadsFromStorage } from '@/src/hooks/useEmberThread.hook'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { getAuthRouteToNavigate } from '@/src/routes/routes.components'
import { getUser } from '@api/data/user.service'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ICompanySelectOption } from './CompanySelectMenuList'
import { useCompanySelectStore } from './companySelectStore'

const constructOptions = (data: { id: number; title: string; firebaseId: string }[], searchTerm: string) =>
  data
    ?.map((item, index) => ({ value: index, label: item.title, id: item.id, firebaseId: item.firebaseId }))
    .filter(i => matchesSparseSearch(searchTerm, i.label))
    .sort((a, b) => {
      return a.label.localeCompare(b.label.trim())
    })

const matchesSparseSearch = (term: string, stringToSearch: string) => {
  const search = term.toLowerCase()
  const target = stringToSearch.toLowerCase()
  return target.includes(search)
}

export const useCompanySelect = () => {
  const [hoveredCompanyIndex, setHoveredCompanyIndex] = useState(0)
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(0)
  const [activeSearchBoxId, setActiveSearchBoxId] = useState(-1)
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const session = useSessionStore()
  const { reportEvent } = useProductAnalytics()
  const locCtx = useLocationsContext()
  const toast = useBaseToast()
  const { t } = useTranslation()
  const dashboardEditingSettings = useDashboardEditingStore()

  const { setIsSwitchingCompany } = useCompanySelectStore()

  const companyOptions = useMemo<ICompanySelectOption[]>(() => {
    const companies = session.user?.companies ?? []
    return constructOptions(companies, companySearchTerm)
  }, [companySearchTerm, session.user?.companies])

  const handleCompanyHoveredChange = (companyIndex: number) => {
    setHoveredCompanyIndex(companyIndex)
  }

  const handleSubmit = async () => {
    const hoveredOption = companyOptions.find(option => option.value === hoveredCompanyIndex)

    if (hoveredOption && session.user?.pgId && hoveredOption.id && session.user?.company.id !== hoveredOption.id) {
      try {
        setIsSwitchingCompany(true)
        await API.users.selectCompany({ userId: session.user?.pgId, companyId: hoveredOption.id })

        reportEvent('company_selector_used')
        setSelectedCompanyIndex(hoveredCompanyIndex)

        const [userData, companyData, { enabledModules }] = await Promise.all([
          getUser(session.user.id),
          getCompany(hoveredOption.firebaseId),
          API.location.getEnabledModules({
            userId: session.user.pgId,
            companyId: hoveredOption.id,
          }),
        ])

        if (userData && companyData) {
          const userParams = {
            ...userData,
            pgId: userData.pgId,
            id: session.user.id,
            companyName: companyData.name ?? companyData.title,
            companyFirebaseId: companyData.firebaseId,
            appReloadRequired: false,
            customDashboardLayout: userData.customDashboardLayout as any,
          }

          clearEmberThreadsFromStorage()
          session.updateUserInStore(userParams)
          dashboardEditingSettings.initialize(userParams)
          locCtx.resetStorageData()

          const authenticatedRouteNavigate = getAuthRouteToNavigate(enabledModules as LocationModule[])
          window.location.assign(authenticatedRouteNavigate)
        }
      } catch (error) {
        setIsSwitchingCompany(false)
        toast({ description: t('failed_to_switch_company'), status: 'error' })
      }
    }
  }

  const handleCompanySearch = (searchTerm: string) => {
    setCompanySearchTerm(searchTerm)
  }

  useEffect(() => {
    const matchedCompany = companyOptions.find(option => option.id === session?.user?.company?.id)
    setSelectedCompanyIndex(matchedCompany?.value ?? 0)
  }, [companyOptions, session?.user?.company?.id])

  return {
    hoveredCompanyIndex,
    selectedCompanyIndex,
    activeSearchBoxId,
    companyOptions,
    companySearchTerm,
    setActiveSearchBoxId,
    handleCompanyHoveredChange,
    handleCompanySearch,
    handleSubmit,
  }
}
